import type { IMobileNumber } from '~/interfaces/dto/auth/response';
import type { IBaseResponse } from '~/interfaces/dto/base/request';

interface IAddNumberRequest {
  type: 'add';
  requestBody: { mobileNumber: string };
}
interface IDeleteNumberRequest {
  type: 'delete';
  requestBody: { mobileNumber: string };
}
interface IChangePrimaryNumberRequest {
  type: 'change-primary';
  requestBody: { newPrimaryMobileNumber: string };
}
interface IVerifyOtpRequest {
  type: 'verify-otp';
  requestBody: { mobileNumber: string; otp: string; isPrimary?: boolean };
}
interface IRequestOtpRequest {
  type: 'request-otp';
  requestBody?: { mobileNumber?: string };
}
type TUpdateNumberRequest =
  | IAddNumberRequest
  | IDeleteNumberRequest
  | IChangePrimaryNumberRequest
  | IVerifyOtpRequest
  | IRequestOtpRequest;

const data = reactive({
  numberToVerify: null as IMobileNumber | null,
  primaryNumber: null as IMobileNumber | null,
  secondaryNumbers: [] as IMobileNumber[],
  desiredPrimaryNumber: '',
  numberToUpdate: '',
  errorCode: '',
  errorRef: '',
  newNumber: '',
  toDelete: '',
  isLoading: false,
  inFlightAction: '' as TUpdateNumberRequest['type'] | '',
});

export default () => {
  const { $authService } = useServices();
  const site = useSiteStore();

  const prefix = computed(() =>
    (site.getDialingCode || '').replace(/\D+/g, ''),
  );

  const normalizeNumber = (input: string): string | null => {
    const number = (input || '').replace(/\D+/g, '').trim();
    if (!prefix.value) return null;
    if (number.startsWith(prefix.value)) return number;
    if (number.startsWith('0')) return prefix.value + number.slice(1);
    if (/^[6-8]\d{8}$/.test(number)) return prefix.value + number;
    return null;
  };

  const allNumbers = computed<IMobileNumber[]>(() => {
    return data.primaryNumber
      ? [data.primaryNumber, ...data.secondaryNumbers].map((number) => {
          return {
            ...number,
            mobileNumber: normalizeNumber(number.mobileNumber),
          };
        })
      : [];
  });

  const isNumberAlreadyUsed = (input: string) => {
    const normalizedNumber = normalizeNumber(input);
    return (
      !!normalizedNumber &&
      allNumbers.value.find(
        (number) => number.mobileNumber === normalizedNumber,
      )
    );
  };

  const isNumberValid = (input: string) => {
    const normalizedNumber = normalizeNumber((input || '').trim());
    if (!prefix.value || !normalizedNumber) return false;

    const numberExcludingPrefix = normalizedNumber.slice(prefix.value.length);
    const numberValid = /^[6-8]\d{8}$/.test(numberExcludingPrefix);
    if (!numberValid) return false;

    return !allNumbers.value.find(
      (number) => number.mobileNumber === normalizedNumber,
    );
  };

  const disableAddAndUpdateButton = computed(() => {
    const valid = (data.newNumber || '').trim();
    return !valid || !!data.errorCode || !isNumberValid(valid);
  });

  const clearErrors = () => {
    data.errorCode = '';
    data.errorRef = '';
  };

  const setErrorFromResponse = (response?: IBaseResponse | null) => {
    if (!response) return;
    if (!response.isSuccessful) {
      data.errorCode = String(
        response?.error?.displayCode || response?.error?.code || 'unknownError',
      );
      data.errorRef = String(response?.error?.code || '');
    }
  };

  let statusesPromise: Promise<void> | null = null;

  const getMobileNumberStatuses = async () => {
    if (statusesPromise) return statusesPromise;
    statusesPromise = (async () => {
      const response = await $authService.getMobileNumberStatus();
      if (response?.isSuccessful) {
        const list: IMobileNumber[] = response.data || [];
        data.primaryNumber = list.find((n) => !!n.primaryNumber) || null;
        data.secondaryNumbers = list.filter((n) => !n.primaryNumber);

        data.numberToVerify =
          list.find(
            (number) =>
              !number.primaryNumber &&
              !number.verified &&
              !!number.expiryDateTime,
          ) || null;
      } else {
        setErrorFromResponse(response);
      }
    })();

    try {
      await statusesPromise;
    } finally {
      statusesPromise = null;
    }
  };

  const updateHandler = async (
    request: TUpdateNumberRequest,
  ): Promise<Partial<IBaseResponse>> => {
    if (data.isLoading) {
      return {
        isSuccessful: false,
      };
    }

    data.isLoading = true;
    data.inFlightAction = request.type;
    clearErrors();

    let response: IBaseResponse = { isSuccessful: false } as IBaseResponse;

    try {
      switch (request.type) {
        case 'add': {
          response = await $authService.addMobileNumber(request.requestBody);
          if (response.isSuccessful) site.activateModal('verifyNumberOtp');
          break;
        }
        case 'delete': {
          response = await $authService.deleteMobileNumber(request.requestBody);
          break;
        }
        case 'change-primary': {
          response = await $authService.changePrimaryNumber(
            request.requestBody,
          );
          break;
        }
        case 'verify-otp': {
          const { mobileNumber, otp, isPrimary } = request.requestBody;
          response = await $authService.verifyOtp({
            mobileNumber,
            otp,
            isPrimary: !!isPrimary,
          });
          break;
        }
        case 'request-otp': {
          response = await $authService.requestNewOtp({
            mobileNumber: data.numberToVerify?.mobileNumber,
          });
          break;
        }
      }

      await getMobileNumberStatuses();

      setErrorFromResponse(response);
    } catch (err) {
      setErrorFromResponse(err);
    } finally {
      data.isLoading = false;
      data.inFlightAction = '';
    }
    return response;
  };

  const addNumber = (mobileNumber: string) =>
    updateHandler({ type: 'add', requestBody: { mobileNumber } });
  const deleteNumber = (mobileNumber: string) =>
    updateHandler({ type: 'delete', requestBody: { mobileNumber } });
  const setPrimary = (newPrimaryMobileNumber: string) =>
    updateHandler({
      type: 'change-primary',
      requestBody: { newPrimaryMobileNumber },
    });
  const verifyOtp = (mobileNumber: string, otp: string, isPrimary?: boolean) =>
    updateHandler({
      type: 'verify-otp',
      requestBody: { mobileNumber, otp, isPrimary },
    });
  const requestOtp = () =>
    updateHandler({
      type: 'request-otp',
      requestBody: { mobileNumber: data.numberToVerify?.mobileNumber },
    });

  const resetData = () => {
    data.numberToVerify = null;
    data.primaryNumber = null;
    data.secondaryNumbers = [];
    data.desiredPrimaryNumber = '';
    data.numberToUpdate = '';
    data.errorCode = '';
    data.errorRef = '';
    data.newNumber = '';
    data.toDelete = '';
    data.isLoading = false;
    data.inFlightAction = '';
  };

  return {
    data,
    allNumbers,
    disableAddAndUpdateButton,
    resetData,
    isNumberValid,
    isNumberAlreadyUsed,
    getMobileNumberStatuses,
    addNumber,
    deleteNumber,
    setPrimary,
    verifyOtp,
    requestOtp,
    normalizeNumber,
  };
};
